import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CATEGORIES as DEFAULT_CATEGORIES } from '../constants';
import { generateId } from '../utils';
import {
  loadMachines,
  createMachine,
  updateMachine as apiUpdateMachine,
  deleteMachine as apiDeleteMachine,
  loadCategories,
  createCategory,
  updateCategory as apiUpdateCategory,
  deleteCategory as apiDeleteCategory,
  loadAdminPassword,
  saveAdminPassword,
} from '../lib/appBackend';

const MachineContext = createContext(null);

const AUTH_KEY = 'bm_admin_auth';
const DEFAULT_PASSWORD = 'change-me-now';

// ── PROVIDER ──────────────────────────────────────────────────────────────────
export function MachineProvider({ children }) {

  // ── loading state ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);

  // ── machines ──────────────────────────────────────────────────────────────────
  const [machines, setMachines] = useState([]);

  useEffect(() => {
    loadMachines()
      .then((data) => {
        setMachines(data ?? []);
        setLoading(false);
      })
      .catch((error) => {
        console.error('[machines fetch]', error.message);
        setLoading(false);
      });
  }, []);

  // ── categories ────────────────────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadCategories()
      .then((data) => {
        setCategories(data?.length ? data : DEFAULT_CATEGORIES);
      })
      .catch((error) => {
        console.error('[categories fetch]', error.message);
        setCategories(DEFAULT_CATEGORIES);
      });
  }, []);

  const addCategory = useCallback(async (labelOrObj, icon = '📦') => {
    const label        = typeof labelOrObj === 'object' ? labelOrObj.label : labelOrObj;
    const resolvedIcon = typeof labelOrObj === 'object' ? (labelOrObj.icon ?? icon) : icon;
    const id = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

    const inserted = await createCategory({ id, label, icon: resolvedIcon });
    if (!inserted?.ok) { console.error('[addCategory]', inserted?.error || 'Failed'); return; }
    setCategories(prev => [...prev, { id, label, icon: resolvedIcon }]);
  }, []);

  const updateCategory = useCallback(async (id, labelOrObj, icon) => {
    const label        = typeof labelOrObj === 'object' ? labelOrObj.label : labelOrObj;
    const resolvedIcon = typeof labelOrObj === 'object' ? (labelOrObj.icon ?? icon) : icon;

    const updated = await apiUpdateCategory(id, { label, icon: resolvedIcon });
    if (!updated?.ok) { console.error('[updateCategory]', updated?.error || 'Failed'); return; }
    setCategories(prev => prev.map(c => c.id === id ? { ...c, label, icon: resolvedIcon } : c));
  }, []);

  const deleteCategory = useCallback(async (id) => {
    const deleted = await apiDeleteCategory(id);
    if (!deleted?.ok) { console.error('[deleteCategory]', deleted?.error || 'Failed'); return; }
    setCategories(prev => prev.filter(c => c.id !== id));
  }, []);

  // ── admin password (stored in the application database) ───────────────────────
  const [adminPassword, setAdminPassword] = useState(null); // null = not loaded yet

  useEffect(() => {
    loadAdminPassword()
      .then((value) => {
        if (!value) {
          saveAdminPassword(DEFAULT_PASSWORD)
            .then(() => setAdminPassword(DEFAULT_PASSWORD))
            .catch(() => setAdminPassword(DEFAULT_PASSWORD));
        } else {
          setAdminPassword(value);
        }
      })
      .catch(() => {
        setAdminPassword(DEFAULT_PASSWORD);
      });
  }, []);

  // ── admin auth ────────────────────────────────────────────────────────────────
  const [isAdmin, setIsAdmin] = useState(() => {
    try { return sessionStorage.getItem(AUTH_KEY) === 'true'; }
    catch { return false; }
  });

  const login = useCallback((password) => {
    if (adminPassword === null) return false; // still loading
    if (typeof password !== 'string') return false;
    if (password.trim() === '' || password.length > 64) return false;
    if (password === adminPassword) {
      setIsAdmin(true);
      try { sessionStorage.setItem(AUTH_KEY, 'true'); } catch {}
      return true;
    }
    return false;
  }, [adminPassword]);

  const logout = useCallback(() => {
    setIsAdmin(false);
    try { sessionStorage.removeItem(AUTH_KEY); } catch {}
  }, []);

  /**
   * Change password. Returns { ok, error } where error is a user-facing string.
   * Rules: 1-12 chars, alphanumeric only (letters + digits).
   */
  const changePassword = useCallback(async (currentPwd, newPwd) => {
    if (currentPwd !== adminPassword) {
      return { ok: false, error: 'Current password is incorrect.' };
    }
    if (!newPwd || newPwd.length < 1 || newPwd.length > 12) {
      return { ok: false, error: 'New password must be 1–12 characters.' };
    }
    if (!/^[a-zA-Z0-9]+$/.test(newPwd)) {
      return { ok: false, error: 'Password may only contain letters and numbers.' };
    }

    const saved = await saveAdminPassword(newPwd);

    if (!saved?.ok) {
      console.error('[changePassword]', saved?.error || 'Failed');
      return { ok: false, error: 'Failed to save password. Please try again.' };
    }

    setAdminPassword(newPwd);
    return { ok: true };
  }, [adminPassword]);

  // ── toasts ────────────────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  // ── CRUD ──────────────────────────────────────────────────────────────────────

  const addMachine = useCallback(async (data) => {
    const id = generateId();
    const { images: imageList = [], ...rest } = data;

    const row = {
      ...rest,
      id,
      images: imageList,
    };

    const inserted = await createMachine(row);
    if (!inserted?.ok) { console.error('[addMachine]', inserted?.error || 'Failed'); return; }
    setMachines(prev => [{ ...row, created_at: new Date().toISOString() }, ...prev]);
  }, []);

  const updateMachine = useCallback(async (id, data) => {
    const { images: imageList = [], ...rest } = data;

    const updated = await apiUpdateMachine(id, { ...rest, images: imageList });
    if (!updated?.ok) { console.error('[updateMachine]', updated?.error || 'Failed'); return; }
    setMachines(prev => prev.map(m => m.id === id ? { ...m, ...rest, images: imageList } : m));
  }, []);

  const deleteMachine = useCallback(async (id) => {
    const deleted = await apiDeleteMachine(id);
    if (!deleted?.ok) { console.error('[deleteMachine]', deleted?.error || 'Failed'); return; }
    setMachines(prev => prev.filter(m => m.id !== id));
  }, []);

  const getMachine = useCallback((id) => {
    return machines.find(m => m.id === id) ?? null;
  }, [machines]);

  return (
    <MachineContext.Provider value={{
      machines,
      loading,
      addMachine,
      updateMachine,
      deleteMachine,
      getMachine,
      categories,
      addCategory,
      updateCategory,
      deleteCategory,
      isAdmin,
      login,
      logout,
      changePassword,
      adminPasswordLoaded: adminPassword !== null,
      toasts,
      addToast,
    }}>
      {children}
    </MachineContext.Provider>
  );
}

// ── HOOK ──────────────────────────────────────────────────────────────────────
export function useMachines() {
  const ctx = useContext(MachineContext);
  if (!ctx) throw new Error('useMachines must be used inside MachineProvider');
  return ctx;
}
